import {Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal, viewChild} from '@angular/core';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {WjFlexGridFilter, WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {VisibilityObserverDirective} from '../../directives/visibility-observer.directive';
import {AccountGridService, AppAccountColumnDeclaration} from '../account-grid/account-grid-helper';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {AppFormat} from '../../lib/format';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {CollectionView, DataType} from '@mescius/wijmo';
import {Stdlib} from '../../lib/stdlib';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {FormsModule} from '@angular/forms';
import {WjChartModule, WjFlexChartAxis, WjFlexChartSeries} from '@mescius/wijmo.angular2.chart';
import {FlexChart, HitTestInfo, IRenderEngine} from '@mescius/wijmo.chart';
import {WjChartAnnotationModule} from '@mescius/wijmo.angular2.chart.annotation';
import {maxBy} from 'es-toolkit';
import {Operator} from '@mescius/wijmo.grid.filter';
import {WjTabStatePersistDirective} from '../../directives/wjtab-state-persist.directive';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';


const AccountGridColumns: AppAccountColumnDeclaration[] =
    [
        "row",
        { attribute: "crmSubmissionId", width: 150 },
        { attribute: "account", width: 200 },
        "largeFrequency", "largeLossMargin",
        { header:"Adjusted Volatility", binding: "adjustedVolatility", visible: false },
        { header: "Control Account", binding: "isControlAccount", dataType: DataType.Boolean },
        { header: "Quadrant", binding: "quadrant", dataType: DataType.String, width: 120 }
    ];

function strokeColorByFinalPilStatus(status: string | undefined): string {
    switch (status) {
        case 'Protect': return 'green';
        case 'Improve': return 'orange';
        case 'Lose': return 'red';
        case 'Key Account': return 'blue';
        default: return 'black';
    }
}

@Component({
    selector: 'app-dashboard-volatility',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjGridModule,
        WjGridFilterModule,
        VisibilityObserverDirective,
        WjGridSearchModule,
        WjNavModule,
        WjInputModule,
        FormsModule,
        WjChartModule,
        WjChartAnnotationModule,
        WjTabStatePersistDirective
    ],
    templateUrl: './dashboard-volatility.component.html',
    styleUrl: './dashboard-volatility.component.scss'
})
export class DashboardVolatilityComponent {

    protected readonly AppFormat = AppFormat;
    protected readonly Wijmo = Wijmo;

    protected threshold = signal(0.01);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);
    // private readonly volatilityAccountData = signal<CollectionView>(new CollectionView<any>([]));

    protected readonly grid = viewChild<WjFlexGrid>('grid');
    protected readonly gridFilter = viewChild<WjFlexGridFilter>('gridFilter');
    protected readonly volatilityChart = viewChild<FlexChart>('volatilityChart');
    protected readonly volatilityChartSeries = viewChild<WjFlexChartSeries>('volatilityChartSeries');
    protected readonly separatorSeries = viewChild<WjFlexChartSeries>('separatorSeries');
    protected readonly axisX = viewChild<WjFlexChartAxis>('axisX');
    protected readonly axisY = viewChild<WjFlexChartAxis>('axisY');

    protected quadrantDivisorStroke = {
        stroke: 'orange',
        strokeWidth: 2,
        strokeDasharray: '5,5'
    };

    constructor() {
        effect(() => {
            const itemsSource = this.pfSegData.getAccountData();
            const threshold = this.threshold();
            const modifiedCv = new CollectionView(itemsSource, {
                calculatedFields: {
                    isControlAccount: ($: any) => ($.largeFrequency ?? 0.0) > threshold,
                    quadrant: this.quadrant.bind(this)
                }
            });
            WjGridUtils.updateGridItemsSourceAndPreserveSelection(this.grid(), modifiedCv);
            this.prepareChartData(modifiedCv);
        });
    }

    protected quadrant(item: any) {
        if (item.isControlAccount) {
            return item.largeFrequency > item.largeLossMargin ? 'Focus' : 'Risk remunerative';
        } else {
            return "-"
        }
    }

    protected async onGridInitialized(grid: WjFlexGrid, gridFilter: WjFlexGridFilter) {
        await AccountGridService.configureGridColumns(grid, AccountGridColumns, this.pfSegData);
        WjGridColumnChooser.addColumnChooser('dashboard.volatility', grid);

        const isControlAccountFilter = gridFilter.getColumnFilter('isControlAccount');
        isControlAccountFilter.valueFilter.showValues = { true: true};
        gridFilter.apply();
    }

    private prepareChartData(accountData: CollectionView) {
        const controlAccounts = accountData.items.filter(it => it.isControlAccount);
        const chartData = controlAccounts.map(it => ({
            x: it.largeFrequency,
            y: it.largeLossMargin,
            account: it.account,
            finalPilStatus: it.finalPilStatus
        }));

        const maxLargeFrequency = maxBy(chartData, it => it.x)?.x ?? 0;
        const maxLargeLossMargin = maxBy(chartData, it => it.y)?.y ?? 0;
        const maxMax = Math.min(Math.max(maxLargeFrequency, maxLargeLossMargin), 0.1);

        Stdlib.let(this.volatilityChartSeries(), (series)=> {
            series.itemsSource = chartData;
            this.separatorSeries()!.itemsSource = [{ x: 0, y: 0 }, { x: maxMax, y: maxMax }];
            this.axisX()!.max = maxMax;
            this.axisY()!.max = maxMax;
        });
    }

    protected tooltipContent(hti: HitTestInfo) {
        const data = hti.item;
        return `${data.account}</br> (${data.finalPilStatus})`;
    }

    itemFormatter(engine: IRenderEngine, ht: HitTestInfo, render: Function){
        const item = ht.item;
        engine.stroke = strokeColorByFinalPilStatus(ht.item.finalPilStatus) ?? 'black';
        engine.drawEllipse(ht.point.x, ht.point.y, 4, 4);
        // render();
    }
}
